import type { RouterState } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import { Route, TrendingUp } from 'lucide-react'
import { useMemo } from 'react'
import { authClient } from '@/lib/auth-client'

export type SubscriptionPlan = 'free' | 'pro' | 'enterprise'

export interface PlanDefinition {
  id: SubscriptionPlan
  label: string
  description: string
  price: string
  priceId?: string
  features: string[]
  badge?: string
  highlight?: boolean
  icon: LucideIcon
}

const proPriceId = import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY

export const PLAN_DEFINITIONS: PlanDefinition[] = [
  {
    id: 'free',
    label: 'Pay-as-you-go',
    description:
      'Try smarter scheduling first. Pay only when it turns into completed work you can actually reach.',
    price: '$1.99/booking',
    features: [
      'Openings that respect drive time',
      'Client addresses before times are offered',
      'Google Calendar kept in sync',
      'Start and end points for the day',
      'Simple monthly billing',
      'No monthly minimum',
    ],
    icon: Route,
  },
  {
    id: 'pro',
    label: 'Pro',
    description:
      'For busier weeks when calls, gaps, and repeat-client details start costing real time.',
    price: '$99/month',
    priceId: proPriceId,
    features: [
      'Everything in Pay-as-you-go',
      'Unlimited bookings',
      'Capture requests from calls',
      'Recover nearby gaps',
      'Remember repeat-client details',
      'Spot route-friendly openings',
      'Account for service time',
      'Priority chat support',
    ],
    highlight: true,
    badge: 'For busy weeks',
    icon: TrendingUp,
  },
]

export interface SubscriptionDetails {
  id?: string
  planId: SubscriptionPlan
  status?: string
  currentPeriodEnd?: string | Date | null
  trialEndsAt?: string | Date | null
  cancelAtPeriodEnd?: boolean
}

interface ExtendedUser {
  id?: string
  name?: string | null
  email?: string | null
  subscription?: {
    id?: string
    planId?: string | null
    status?: string | null
    currentPeriodEnd?: string | Date | null
    trialEndsAt?: string | Date | null
    cancelAtPeriodEnd?: boolean | null
  } | null
}

const PLAN_ORDER: Record<SubscriptionPlan, number> = {
  free: 0,
  pro: 1,
  enterprise: 2,
}

export const normalizePlanId = (value?: string | null): SubscriptionPlan => {
  if (!value) return 'free'
  const normalized = value.toLowerCase()
  if (normalized.startsWith('enterprise')) return 'enterprise'
  if (normalized.startsWith('pro')) return 'pro'
  return 'free'
}

const mapSubscription = (raw?: ExtendedUser['subscription']): SubscriptionDetails => ({
  id: raw?.id ?? undefined,
  planId: normalizePlanId(raw?.planId ?? undefined),
  status: raw?.status ?? undefined,
  currentPeriodEnd: raw?.currentPeriodEnd ?? null,
  trialEndsAt: raw?.trialEndsAt ?? null,
  cancelAtPeriodEnd: raw?.cancelAtPeriodEnd ?? undefined,
})

export const hasPlanAccess = (current: SubscriptionPlan, required: SubscriptionPlan) =>
  PLAN_ORDER[current] >= PLAN_ORDER[required]

export function useSubscription() {
  const session = authClient.useSession()

  // Type-safe session loading state
  const isLoading = (() => {
    if ('isPending' in session) return session.isPending
    if ('isLoading' in session) return (session as { isLoading: boolean }).isLoading
    return false
  })()

  const userData = session.data?.user

  return useMemo(() => {
    if (!userData) {
      return {
        planId: 'free' as SubscriptionPlan,
        status: 'inactive',
        plan: 'free' as SubscriptionPlan,
        isLoading,
        isTrialing: false,
        trialEndsAt: null,
        isActive: false,
      }
    }

    const user = userData as ExtendedUser
    const subscription = mapSubscription(user.subscription)
    const status = subscription.status ?? 'inactive'

    return {
      ...subscription,
      status,
      plan: subscription.planId,
      planId: subscription.planId,
      isLoading,
      isActive: status === 'active',
      isTrialing: status === 'trialing',
    }
  }, [userData, isLoading])
}

export function usePlanAccess(requiredPlan: SubscriptionPlan) {
  const subscription = useSubscription()
  return {
    ...subscription,
    hasAccess: hasPlanAccess(subscription.plan, requiredPlan),
  }
}

export const featureRequiresPlan = (
  plan: SubscriptionPlan,
  required?: SubscriptionPlan
): boolean => {
  if (!required) return true
  return hasPlanAccess(plan, required)
}

export const isRouteAllowedForPlan = (
  state: RouterState['location'],
  plan: SubscriptionPlan,
  restrictedPaths: Record<string, SubscriptionPlan>
) => {
  const required = restrictedPaths[state.pathname]
  if (!required) return true
  return hasPlanAccess(plan, required)
}

export const deriveSubscriptionFromUser = (user: unknown): SubscriptionDetails => {
  const casted = (user ?? {}) as ExtendedUser
  return mapSubscription(casted.subscription)
}

export const getPlanDefinition = (plan: SubscriptionPlan): PlanDefinition =>
  PLAN_DEFINITIONS.find((item) => item.id === plan) ?? PLAN_DEFINITIONS[0]
